import type { Session } from '@supabase/supabase-js';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@nuberu/ui';
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';

import { fetchOperators, type OperatorsOverview } from './fetch-operators';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: OperatorsOverview };

// Integer cents → "42,00 €" (es-ES grouping).
const formatEur = (cents: number): string =>
  `${(cents / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const formatDate = (iso: string | null): string => (iso === null ? '—' : iso.slice(0, 10));

const Th = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <th
    className={`pb-2 text-xs font-medium uppercase tracking-wide text-text-secondary ${
      right ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

const Td = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <td className={`py-2 text-sm text-text-primary ${right ? 'text-right tabular-nums' : ''}`}>
    {children}
  </td>
);

export const OperatorsPanel = ({ session }: { session: Session }): ReactElement => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(
    (signal?: AbortSignal): void => {
      setState({ status: 'loading' });
      void fetchOperators(session, signal)
        .then((data) => {
          if (!signal?.aborted) setState({ status: 'ready', data });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          const message =
            err instanceof ApiError
              ? err.status === 403
                ? 'Tu cuenta no tiene acceso al backoffice.'
                : err.message
              : 'No pudimos cargar los operadores.';
          setState({ status: 'error', message });
        });
    },
    [session],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          Operadores
          {state.status === 'ready' ? (
            <span className="ml-2 text-sm font-normal text-text-secondary">
              {state.data.operators.length}
            </span>
          ) : null}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => load()}
          disabled={state.status === 'loading'}
        >
          {state.status === 'loading' ? 'Actualizando…' : 'Actualizar'}
        </Button>
      </CardHeader>
      <CardContent>
        {state.status === 'loading' ? (
          <p className="py-4 text-sm text-text-secondary">Cargando…</p>
        ) : state.status === 'error' ? (
          <p className="py-4 text-sm text-status-warning" role="alert">
            {state.message}
          </p>
        ) : state.data.operators.length === 0 ? (
          <p className="py-2 text-sm text-text-secondary">Sin operadores.</p>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-[1.5px] border-(color:--nuberu-ink)">
                <Th>Operador</Th>
                <Th>Plan</Th>
                <Th>Pagos</Th>
                <Th right>Reservas 30d</Th>
                <Th right>Cobrado 30d</Th>
                <Th right>Última reserva</Th>
              </tr>
            </thead>
            <tbody>
              {state.data.operators.map((op) => (
                <tr
                  key={op.id}
                  className="border-b border-(color:--nuberu-line-soft) last:border-b-0"
                >
                  <Td>
                    {op.name} <span className="text-text-secondary">({op.slug})</span>
                  </Td>
                  <Td>
                    {op.plan !== null ? (
                      <Badge tone={op.subscriptionStatus === 'active' ? 'success' : 'neutral'}>
                        {op.plan}
                      </Badge>
                    ) : (
                      <span className="text-text-secondary">—</span>
                    )}
                    {op.duplicateSubscriptionId !== null ? (
                      <p className="mt-1 text-xs text-status-warning" role="alert">
                        <Badge tone="warning">Cobro doble</Badge> Hay otra suscripción activa en
                        Stripe: <span className="font-mono">{op.duplicateSubscriptionId}</span>.
                        Cancela una de las dos.
                      </p>
                    ) : null}
                  </Td>
                  <Td>
                    {op.activeProcessor !== null ? (
                      op.activeProcessor
                    ) : (
                      <Badge tone="warning">sin pagos</Badge>
                    )}
                  </Td>
                  <Td right>{op.bookings30d}</Td>
                  <Td right>{formatEur(op.collected30dCents)}</Td>
                  <Td right>{formatDate(op.lastBookingAt)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};
